import { useCallback, useMemo } from 'react';
import { useInventoryId } from '@/hooks/use-inventory-id';
import { useSessionQuery } from '@/api/sessions';
import {
  useGetInventoryMeQuery,
  useGetAccessControlsQuery,
  useGetAccessControlCandidatesQuery,
  useRemoveAuthorizationMutation,
  useUpdateAuthorizationMutation,
  useNewAuthorizationMutation,
} from '@/api/inventories';
import { AccessControl } from '@/components/access-control';
import { Loading } from '@/components/loading';
import {
  Access,
  Identity,
  AccessControl as AccessControlEntry,
  IdentityType,
} from '@/types';
import { sortBy } from 'lodash/fp';

const Permissions = () => {
  const inventoryId = useInventoryId();
  const [removeAuthorization] = useRemoveAuthorizationMutation();
  const [updateAuthorization] = useUpdateAuthorizationMutation();
  const [newAuthorization] = useNewAuthorizationMutation();

  const { data: session } = useSessionQuery();
  const { data: inventoryMe, isLoading: isLoadingInventoryMe } =
    useGetInventoryMeQuery(inventoryId);
  const { data: controls, isLoading: isLoadingAccess } =
    useGetAccessControlsQuery(inventoryId);
  const { data: candidates } = useGetAccessControlCandidatesQuery(inventoryId);

  const onAuthorize = useCallback(
    (identity: Identity, access: Access) => {
      newAuthorization({ inventoryId, identity, access });
    },
    [newAuthorization, inventoryId],
  );
  const onRemove = useCallback(
    (identity: Identity) => {
      removeAuthorization({ inventoryId, identity });
    },
    [removeAuthorization, inventoryId],
  );
  const onChange = useCallback(
    (identity: Identity, access: Access) => {
      updateAuthorization({ inventoryId, identity, access });
    },
    [updateAuthorization, inventoryId],
  );

  const sortedControls = useMemo(() => {
    if (!controls) return [];
    const typeToOrder = (t: IdentityType) => {
      switch (t) {
        case 'organisation':
          return 1;
        case 'user':
          return 2;
        case 'service-account':
          return 3;
      }
    };
    return sortBy(
      (c: AccessControlEntry) => [
        typeToOrder(c.identity.type),
        c.identity.name,
      ],
      controls,
    );
  }, [controls]);
  if (isLoadingInventoryMe || isLoadingAccess) {
    return <Loading />;
  } else if (session && controls) {
    return (
      <AccessControl
        isEditor={inventoryMe?.access?.isEditor ?? false}
        me={session.id}
        access={sortedControls}
        candidates={candidates ?? []}
        onAuthorize={onAuthorize}
        onRemove={onRemove}
        onChange={onChange}
      />
    );
  } else {
    return null;
  }
};

export { Permissions };
